import React, { ChangeEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CardContent, CircularProgress, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, Paper, Stack, Switch, TextField, ToggleButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';

import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { widgetRegistry, WidgetRegistryItem } from "./WidgetRegistry";
import { currentUser, permissionsTest, UserPreference } from 'system/InfegyUser';
import Chip from "components/uiElements/dataDisplay/Chip";
import TagList from "routes/tagManagement/TagList";
import { Tag } from "routes/tagManagement/TagManagementPage";

interface AddWidgetDialogProps {
  onAddWidget(type: string, widgetOptions?: any, startingLayouts?: any, openSettingsOnAdd?: boolean): void;
  onClose(): void;
  open: boolean;
}

export default function AddWidgetDialog(props: AddWidgetDialogProps): React.JSX.Element {
  const showTagListDefault = false;

  const { i18n } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const user = currentUser();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [showTagList, setShowTagList] = useState<boolean>(showTagListDefault);
  const [selectedTags, setSelectedTags] = useState<Array<Tag>>([]);
  const [search, setSearch] = useState<string>("");
  const [favorites, setFavorites] = useState<Array<string>>(JSON.parse(user?.getPreference(UserPreference.FAVORITE_QUERIES) || "[]"))
  const [multiple, setMultiple] = useState<boolean>(false);
  const [isAddingWidget, setIsAddingWidget] = useState<boolean>(false);

  const tagColors = [
    theme.colors.namedColors.chart1,
    theme.colors.namedColors.chart2,
    theme.colors.namedColors.chart3,
    theme.colors.namedColors.chart4,
    theme.colors.namedColors.chart5,
    theme.colors.namedColors.chart6,
    theme.colors.namedColors.chart7,
    theme.colors.namedColors.chart8,
    theme.colors.namedColors.chart9
  ];

  useEffect(() => {
    setShowTagList(showTagListDefault);
  }, [isSmallScreen]);

  const widgetTags = useMemo((): Array<Tag> => {
    const widgetTags = new Set<Tag>();
    for (let widget of widgetRegistry) {
      const tags = widget.tags || [];
      for (let tag of tags) {
        widgetTags.add(tag);
      }
    }
    const sortedTags = [...widgetTags].sort((a, b) => {
      if (a.name > b.name) return 1;
      if (a.name < b.name) return -1;
      return 0;
    });
    const tags = sortedTags.map((tag, index): Tag => {
      return {
        ...tag,
        color: tagColors[index % tagColors.length],
        name: i18n.t(`widget-categories.${tag.name}.label`)
      }
    });
    return tags;
  }, []);

  const widgets = useMemo((): Array<WidgetRegistryItem> => {
    const filteredWidgets = widgetRegistry.filter((widget) => {
      const widgetTitle = i18n.t(`widget-types.${widget.id}.label`);
      const widgetTagNames = widget.tags?.map((tag) => i18n.t(`widget-categories.${tag.name}.label`));
      const widgetTagMatch = widgetTagNames?.find((tagLabel) => tagLabel.toLowerCase().includes(search.toLowerCase()));
      const searchMatch = widgetTitle.toLowerCase().includes(search.toLowerCase()) 
        || widgetTagMatch;
      if (search && !searchMatch) {
        // The widget doesn't match the search input.
        return false;
      }
      const intersection = selectedTags?.filter((tag) => !!widget.tags?.find((t) => t.id === tag.id)) ?? [];
      return intersection.length === (selectedTags?.length ?? 0);
    });
    const sortedWidgets = filteredWidgets.sort((a, b) => {
      if (favorites.includes(a.id) && !favorites.includes(b.id)) return -1;
      if (!favorites.includes(a.id) && favorites.includes(b.id)) return 1;
      const aTitle = i18n.t(`widget-types.${a.id}.label`);
      const bTitle = i18n.t(`widget-types.${b.id}.label`);
      if (aTitle > bTitle) return 1;
      if (aTitle < bTitle) return -1;
      return 0;
    });
    return sortedWidgets;
  }, [search, selectedTags, favorites]);

  const widgetThumbnails = useMemo((): Array<ReactNode> => {
    const thumbnails = widgets.map((widget) => {
      const widgetTitle = i18n.t(`widget-types.${widget.id}.label`);
      const hasPermissions = permissionsTest(widget.requiredPermissions);
      const isFavorite = favorites.includes(widget.id);
      return (
        <Grid key={`widgetThumbnail_${widget.id}`} sx={{display: "flex"}} size={{sm: 6, md: 4}}>
          <Card sx={{
            width: "100%",
            "&:hover": {backgroundColor: hasPermissions ? theme.palette.action.hover : ""}
          }}>
            <Box
              onClick={async () => {
                if (hasPermissions) {
                  setIsAddingWidget(true);
                  await props.onAddWidget(widget.id, {data: widget.defaultData}, undefined, !multiple);
                  setIsAddingWidget(false);
                  enqueueSnackbar(i18n.t("dashboard.add-widget-dialog-widget-successfully-added", {widgetTitle: widgetTitle}), {variant: "success", key: Math.random()});
                  !multiple && props.onClose();
                }
              }}
              sx={{
                position: "relative",
                display: "flex",
                height: "100%",
                alignItems: "flex-start"
              }}>
              <CardContent sx={{display: "flex", flexDirection: "column", height: "100%", cursor: "pointer"}}>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "end",
                    justifyContent: "space-between"
                  }}>
                  <Typography gutterBottom variant="h5">
                    {widgetTitle}
                  </Typography>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      if (isFavorite) {
                        const index = favorites.findIndex((f) => f === widget.id);
                        const newFavorites = favorites.toSpliced(index, 1)
                        user?.setPreference(UserPreference.FAVORITE_QUERIES, JSON.stringify(newFavorites));
                        user?.savePreferences();
                        setFavorites(newFavorites);
                      } else {
                        const newFavorites = favorites.toSpliced(0, 0, widget.id);
                        user?.setPreference(UserPreference.FAVORITE_QUERIES, JSON.stringify(newFavorites));
                        user?.savePreferences();
                        setFavorites(newFavorites);
                      }
                    }}
                    onMouseDown={event => event.stopPropagation()}
                    size="small"
                    sx={{color: isFavorite ? "gold" : theme.palette.grey[300]}}
                  >
                    <StarIcon />
                  </IconButton>
                </Stack>
                {widget.image &&
                  <img
                    width="100%"
                    src={widget.image}
                    style={{ objectFit:'contain', filter: hasPermissions ? "" : "grayscale(100%)" }}
                    alt={widgetTitle} />
                }
                <Typography sx={{
                  marginTop: 2
                }}>
                  {i18n.t(`widget-types.${widget.id}.description`)}
                </Typography>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "flex-end",
                    flex: 1,
                    flexWrap: "wrap",
                    gap: 0.5,
                    marginTop: 1
                  }}>
                  {widget.tags?.map((widgetTag, index) => {
                    const foundIndex = widgetTags.findIndex((tag: Tag) => tag.id === widgetTag.id);
                    const tagColor = tagColors[foundIndex % tagColors.length];
                    return (
                      <Chip
                        color={tagColor}
                        key={`widgetTag_${index}`}
                        label={i18n.t(`widget-categories.${widgetTag.name}.label`)}
                      />
                    )
                  })}
                </Stack>
              </CardContent>
              {!hasPermissions &&
                <Box
                  sx={{
                    padding: 2,
                    position: "absolute",
                    top: 0, left: 0, right: 0, bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: theme.colors.alpha.white[70],
                    fontWeight: "bold",
                  }}
                >
                  <Paper sx={{borderStyle: "solid", borderColor: theme.palette.primary.main}}>
                    <Typography variant="h4" sx={{
                      padding: 1
                    }}>
                      {i18n.t("upgrade-required")}
                    </Typography>
                  </Paper>
                </Box>
              }
            </Box>
          </Card>
        </Grid>
      );
    });
    return thumbnails;
  }, [widgets, props.onAddWidget, multiple]);

  const widgetListComponent = useMemo((): ReactNode => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%"
        }}>
        <Paper sx={{p: 1, m: 1, mt: 0, mb: 2 }}>
          <TextField
            fullWidth
            autoFocus={true}
            autoComplete="off"
            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
            placeholder={i18n.t("dashboard.add-widget-dialog-search")}
            size="small"
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment>
              }
            }}
            value={search}
          />
        </Paper>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            overflow: "auto",
            paddingBottom: 1,
            paddingLeft: 1,
            paddingRight: 1
          }}>
          <Grid container spacing={2}>
            {!!widgetThumbnails.length && widgetThumbnails}
            {!widgetThumbnails.length &&
              <Alert severity="warning" sx={{width: "100%"}}>{i18n.t("dashboard.add-widget-dialog-no-results")}</Alert>
            }
          </Grid>
          {multiple && isAddingWidget &&
            <Box
              sx={{
                padding: 2,
                position: "absolute",
                top: 0, left: 0, right: 0, bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.colors.alpha.white[70],
                fontWeight: "bold",
              }}
            >
              <Paper sx={{borderStyle: "solid", borderColor: theme.palette.primary.main, padding: 2}}>
                <Stack
                  sx={{
                    alignItems: "center",
                    gap: 2
                  }}>
                  <CircularProgress />
                  <Typography variant="h4">
                    {i18n.t("dashboard.add-widget-dialog-adding-widget-progress")}
                  </Typography>
                </Stack>
              </Paper>
            </Box>
          }
        </Box>
      </Box>
    );
  }, [search, widgetThumbnails, isAddingWidget]);

  let dialogContent: ReactNode = (
    <Stack direction="row" sx={{
      height: "100%"
    }}>
      <TagList onClick={(innerSelectedTags) => {
        if (selectedTags.find((tag) => tag.id === innerSelectedTags.id)) {
          setSelectedTags(selectedTags.filter((tag) => tag.id !== innerSelectedTags.id));
        } else {
          setSelectedTags([...selectedTags, innerSelectedTags])
        }
      }} selectedTags={selectedTags} tags={widgetTags} sx={{width: 225}} />
      {widgetListComponent}
    </Stack>
  );
  if (isSmallScreen) {
    dialogContent = showTagList ? <TagList onClick={(innerSelectedTags) => {
      if (selectedTags.find((tag) => tag.id === innerSelectedTags.id)) {
        setSelectedTags(selectedTags.filter((tag) => tag.id !== innerSelectedTags.id));
      } else {
        setSelectedTags([...selectedTags, innerSelectedTags])
      }
    }} selectedTags={selectedTags} tags={widgetTags} sx={{width: 225}} /> : widgetListComponent;
  }

  return (
    <ResponsiveDialog
      fullWidth
      disableClickaway={false}
      maxWidth="lg"
      onClose={() => props.onClose()}
      open={props.open}
    >
      <DialogTitle sx={{paddingTop: 1, paddingRight: 3, paddingBottom: 1, paddingLeft: 3}}>
        <Stack direction="row" sx={{
          alignItems: "end"
        }}>
          <Stack
            direction="row"
            sx={{
              flex: 1,
              gap: 2
            }}>
            {isSmallScreen &&
              <ToggleButton onClick={() => setShowTagList(!showTagList)} value="isSmall">
                <MenuOpenIcon />
              </ToggleButton>
            }
            <Box sx={{ pb: 0.5, pt: isSmallScreen ? 1.5 : 0 }}>
              <Typography variant="h4">
                {i18n.t("dashboard.add-widget-dialog-title")}
              </Typography>
            </Box>
          </Stack>
          <FormGroup sx={{ pb: isSmallScreen ? 1 : 0 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={multiple}
                  onChange={(event) => setMultiple(event.target.checked)} />
              }
              label={i18n.t("dashboard.add-widget-dialog-multiple")}
            />
          </FormGroup>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{paddingBottom: 0, paddingLeft: 2, paddingRight: 2, paddingTop: 1, height: "70vh", backgroundColor: theme.palette.background.default}}>
        {dialogContent}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => props.onClose()}>
          {i18n.t("close")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}